// Okenní rám Phoenix okenního frameworku + dvouřádková hlavička.
//   Řádek 1 (pwm-crumbrow): drobečková navigace + ovládání okna
//   Řádek 2 (pwm-titlerow): typový chip (P/M) + titulek + (subtitle/badge F3) + akce (F3)
// Vizuál řídí třídy .pwm-* z index.css (varianta C přes [data-hvariant] na .pwm-app).

import { Fragment, type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Icon } from '@/shared/ui/icon';

import type { WindowRect } from '../core/windowManagerTypes';
import type { DesktopWindow } from '../core/deriveDesktopProjection';

import { useLegacyWindowChromeStore } from '../legacy/legacyWindowChromeStore';

import { WindowControls } from './WindowControls';
import { WindowActions } from './WindowActions';
import { WindowInfoChip } from './WindowInfoChip';
import type { ResizeDir } from './usePointerDrag';

const RESIZE_DIRS: readonly ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

export interface PhoenixWindowProps {
  readonly win: DesktopWindow;
  readonly rect: WindowRect;
  readonly zIndex: number;
  /** Root agenda plochy (plná velikost, bez možnosti osamostatnit nad sebou). */
  readonly isRoot: boolean;
  readonly onFocus: () => void;
  readonly onClose?: () => void;
  readonly onDetach?: () => void;
  readonly onDock?: () => void;
  readonly onMinimize?: () => void;
  readonly onMaximize?: () => void;
  readonly onRestore?: () => void;
  readonly onCrumbNavigate?: (layerId: string) => void;
  /** Pointer-down na hlavičce — fokus + případně začátek tažení (floating). */
  readonly onHeaderPointerDown?: (e: React.PointerEvent) => void;
  /** Dvojklik na hlavičce — maximalizace / obnovení. */
  readonly onHeaderDoubleClick?: () => void;
  /** Začátek resize z některého z 8 úchopů (jen floating). */
  readonly onResizeStart?: (e: React.PointerEvent, dir: ResizeDir) => void;
  /** Skryté okno (minimalizované) — zůstává namountované (iframe se nereloadne). */
  readonly hidden?: boolean;
  /** Bez okenního chrome (hlavička + rámeček) — pro list panel v list-detail režimu. */
  readonly chromeless?: boolean;
  readonly children: ReactNode;
}

export function PhoenixWindow({
  win,
  rect,
  zIndex,
  isRoot,
  onFocus,
  onClose,
  onDetach,
  onDock,
  onMinimize,
  onMaximize,
  onRestore,
  onCrumbNavigate,
  onHeaderPointerDown,
  onHeaderDoubleClick,
  onResizeStart,
  hidden,
  chromeless,
  children,
}: PhoenixWindowProps) {
  const baseStyle: React.CSSProperties =
    win.mode === 'maximized'
      ? { left: 0, top: 0, right: 0, bottom: 0, zIndex, borderRadius: 0 }
      : { left: rect.x, top: rect.y, width: rect.w, height: rect.h, zIndex };
  const style: React.CSSProperties = hidden ? { ...baseStyle, display: 'none' } : baseStyle;

  const crumbs = win.breadcrumb;
  const kindAttr = win.kind === 'native' ? 'native' : 'legacy';

  // Titulek legacy okna se „vycucne" z iframu (window/chrome); fallback na název agendy.
  const chromeTitle = useLegacyWindowChromeStore((s) => s.byLayer[win.layerId]?.title);
  const displayTitle = win.kind === 'legacy' && chromeTitle ? chromeTitle : win.title;

  return (
    <section
      className="pwm-win"
      style={style}
      data-layer-id={win.layerId}
      data-stack-id={win.stackId ?? ''}
      data-kind={kindAttr}
      data-mode={win.mode}
      {...(win.dialog ? { 'data-dialog': true } : {})}
      {...(win.anim ? { 'data-anim': win.anim } : {})}
      {...(win.isGlobalActive ? { 'data-active': true } : {})}
      {...(isRoot ? { 'data-root': true } : {})}
      {...(chromeless ? { 'data-chromeless': true } : {})}
      onPointerDown={onFocus}
    >
      {!chromeless && (
        <header
          className="pwm-head"
          onPointerDown={onHeaderPointerDown}
          onDoubleClick={onHeaderDoubleClick}
        >
          {/* Řádek 1 — drobečky + ovládání okna */}
          <div className="pwm-crumbrow">
            <nav className="pwm-crumb">
              <span className="pwm-crumb-home">
                <Icon name="home" size={11} />
              </span>
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1;
                const clickable = !isLast && onCrumbNavigate;
                return (
                  <Fragment key={c.layerId}>
                    {i > 0 && <span className="pwm-crumb-sep">›</span>}
                    {clickable ? (
                      <a
                        className="pwm-crumb-seg"
                        onClick={() => onCrumbNavigate?.(c.layerId)}
                        title={c.title}
                      >
                        {c.title}
                      </a>
                    ) : (
                      <span className="pwm-crumb-seg" title={c.title}>
                        {c.title}
                      </span>
                    )}
                  </Fragment>
                );
              })}
            </nav>
            <WindowControls
              isBase={win.isBase}
              onDetach={onDetach}
              onDock={onDock}
              onMinimize={onMinimize}
              onMaximize={onMaximize}
              onRestore={onRestore}
              onClose={onClose}
            />
          </div>

          {/* Řádek 2 — typový chip (klik = detaily okna) + titulek + akce */}
          <div className="pwm-titlerow">
            <WindowInfoChip win={win} />
            <span className="pwm-title" title={displayTitle}>
              {displayTitle}
            </span>
            <span className="pwm-title-spacer" />
            <WindowActions win={win} />
          </div>
        </header>
      )}

      <div className="pwm-win-body">
        <div className={cn('pwm-win-content')}>{children}</div>
      </div>

      {/* Resize úchopy vykreslíme, kdykoli je předán handler (floating i dialog v ploše). */}
      {onResizeStart && (
        <>
          {RESIZE_DIRS.map((dir) => (
            <div
              key={dir}
              className={`pwm-resize pwm-resize-${dir}`}
              onPointerDown={(e) => onResizeStart(e, dir)}
            />
          ))}
        </>
      )}
    </section>
  );
}
